import { useEffect, useState } from "react";
import { api, imageURL } from "../api";
import { useAuth } from "../auth";
import { Hero, LoadingRows, MediaRail, PageState, TitleDialog } from "../components/Media";
import type { HomeResponse, LibraryItem, Title } from "../types";

export function DiscoverPage() {
  const { user } = useAuth();
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [selected, setSelected] = useState<Title | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [continueItems, setContinueItems] = useState<LibraryItem[]>([]);

  const load = () => {
    setError(null);
    api<HomeResponse>("/v1/discover/home")
      .then(setHome)
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Discovery is unavailable."));
  };

  useEffect(load, []);
  useEffect(() => {
    if (!user) { setContinueItems([]); return; }
    api<{ items: LibraryItem[] }>("/v1/me/library")
      .then((response) => setContinueItems(response.items.filter((item) => item.status === "watching" && item.title.type === "tv")))
      .catch(() => setContinueItems([]));
  }, [user]);

  if (error) return <PageState title="Discovery took a pause" message={error} action={<button className="primary-button" onClick={load}>Try again</button>} />;
  if (!home) return <div className="discover-page loading-page"><div className="hero-skeleton" /><LoadingRows /></div>;
  if (!home.featured) return <PageState title="The catalog is warming up" message="Korean dramas and films will appear after the next catalog refresh." />;

  return (
    <div className="discover-page">
      <Hero title={home.featured} onOpen={setSelected} />
      <div className="rails-stack">
        {continueItems.length > 0 && <ContinueWatchingRail items={continueItems} onOpen={setSelected} />}
        {home.sections.map((section) => <MediaRail key={section.id} section={section} onOpen={setSelected} />)}
      </div>
      {selected && <TitleDialog title={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ContinueWatchingRail({ items, onOpen }: { items: LibraryItem[]; onOpen(title: Title): void }) {
  return <section className="media-section continue-section"><div className="section-heading"><div><h2>Continue watching</h2><p>Pick up exactly where you stopped</p></div></div><div className="continue-rail">{items.map((item) => { const backdrop = imageURL(item.title.backdropPath ?? item.title.posterPath, "backdrop"); const total = item.title.episodesCount ?? 0; const progress = total > 0 ? Math.min(100, Math.max(3, item.currentEpisode / total * 100)) : 5; return <button key={item.id} className="continue-card" onClick={() => onOpen(item.title)}><span className="continue-art" style={backdrop ? { backgroundImage: `url(${backdrop})` } : undefined}><span>{total > 0 ? `Episode ${item.currentEpisode} of ${total}` : item.currentEpisode > 0 ? `Episode ${item.currentEpisode}` : "Ready to start"}</span><i style={{ width: `${progress}%` }} /></span><strong>{item.title.name}</strong></button>; })}</div></section>;
}
